import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { ErrorDisplay } from '../components/ui/ErrorDisplay';
import { useTranslation } from 'react-i18next';

const Login: React.FC = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const result = await login(email, password);
    
    if (result.success) {
      toast.success(t('auth.welcomeBack'), {
        description: t('auth.loginSuccess')
      });
      
      // Check if user must change password
      if (result.user?.must_change_password) {
        navigate('/change-password');
        return;
      }
      
      // All authenticated users go to the dashboard
      navigate('/');
    } else {
      const errorMessage = result.error || t('auth.invalidCredentials');
      setError(errorMessage);
      toast.error(t('auth.loginFailed'), {
        description: errorMessage
      });
    }
    
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <h2 className="mt-6 text-3xl font-extrabold text-foreground">
            {t('login.signInToVPP')}
          </h2>
          <p className="mt-2 text-sm text-muted-foreground">
            {t('login.enterCredentials')}
          </p>
        </div>
        
        <Card>
          <CardHeader>
            <CardTitle>{t('login.login')}</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <ErrorDisplay 
                error={error} 
                type="error"
                dismissible
                onDismiss={() => setError('')}
              />
              
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-foreground">
                  {t('login.email')}
                </label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="mt-1"
                  placeholder={t('login.enterEmail')}
                />
              </div>
              
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-foreground">
                  {t('login.password')}
                </label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="mt-1"
                  placeholder={t('login.enterPassword')}
                />
              </div>
              
              <Button
                type="submit"
                disabled={loading}
                className="w-full"
              >
                {loading ? t('common.loading') : t('login.signIn')}
              </Button>
            </form>
            
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Login;